/**
 * SearchBar component - Airbnb/Booking.com style editable search bar
 *
 * Displays current search criteria and allows editing in a modal or inline.
 */

import { useState, useRef, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { MapPin, Calendar, Users, Search, ChevronDown, X } from "lucide-react";
import { CustomSelect } from "../CustomSelect";
import { useTranslation } from "../../hooks/useTranslation";
import { TOKYO_WARDS, OCCUPANT_OPTIONS } from "../../constants/locations";

export interface SearchCriteria {
  prefecture: string;
  city: string;
  ward: string;
  moveInDate: Date | null;
  occupants: number;
}

interface SearchBarProps {
  criteria: SearchCriteria;
  onChange: (criteria: SearchCriteria) => void;
  className?: string;
}

export function SearchBar({
  criteria,
  onChange,
  className = "",
}: SearchBarProps) {
  const t = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [localCriteria, setLocalCriteria] = useState(criteria);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync local state when props change
  useEffect(() => {
    setLocalCriteria(criteria);
  }, [criteria]);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsExpanded(false);
        setLocalCriteria(criteria); // Reset on cancel
      }
    }

    if (isExpanded) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isExpanded, criteria]);

  const handleApply = () => {
    onChange(localCriteria);
    setIsExpanded(false);
  };

  const handleClear = () => {
    const defaultCriteria: SearchCriteria = {
      prefecture: "Tokyo",
      city: "Tokyo",
      ward: "",
      moveInDate: null,
      occupants: 1,
    };
    setLocalCriteria(defaultCriteria);
  };

  // Format location for display
  const locationDisplay =
    [criteria.ward, criteria.city, criteria.prefecture]
      .filter(Boolean)
      .join(", ") || t("searchSelectArea");

  // Format date for display
  const dateDisplay = criteria.moveInDate
    ? criteria.moveInDate.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : t("searchMoveInPlaceholder");

  // Format occupants for display
  const occupantsDisplay =
    criteria.occupants === 1
      ? `1 ${t("searchPerson")}`
      : `${criteria.occupants} ${t("searchPeople")}`;

  const today = new Date();

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Collapsed View - Clickable summary bar */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          className="w-full bg-white rounded-2xl sm:rounded-full shadow-md border border-gray-200 px-3 sm:px-4 py-2 sm:py-2 hover:shadow-lg transition-shadow overflow-hidden"
        >
          {/* Desktop layout - single row */}
          <div className="hidden sm:flex items-center gap-2">
            <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden">
              {/* Location */}
              <div className="flex items-center gap-1.5 px-3 py-1.5 border-r border-gray-200 min-w-0 shrink">
                <MapPin className="w-4 h-4 text-red-500 shrink-0" />
                <span className="text-sm font-medium text-gray-900 truncate">
                  {locationDisplay}
                </span>
              </div>

              {/* Date */}
              <div className="flex items-center gap-1.5 px-3 py-1.5 border-r border-gray-200 shrink-0">
                <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="text-sm text-gray-600 whitespace-nowrap">
                  {dateDisplay}
                </span>
              </div>

              {/* Occupants */}
              <div className="flex items-center gap-1.5 px-3 py-1.5 shrink-0">
                <Users className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="text-sm text-gray-600 whitespace-nowrap">
                  {occupantsDisplay}
                </span>
              </div>
            </div>

            {/* Edit indicator */}
            <div className="flex items-center gap-2 shrink-0">
              <ChevronDown className="w-4 h-4 text-gray-400" />
              <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                <Search className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>

          {/* Mobile layout - two rows */}
          <div className="flex sm:hidden flex-col gap-1">
            {/* First row: Location + Search button */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 min-w-0">
                <MapPin className="w-4 h-4 text-red-500 shrink-0" />
                <span className="text-sm font-medium text-gray-900 truncate">
                  {locationDisplay}
                </span>
              </div>
              <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center shrink-0 ml-2">
                <Search className="w-4 h-4 text-white" />
              </div>
            </div>
            {/* Second row: Date + Occupants */}
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{dateDisplay}</span>
              </div>
              <span className="text-gray-300">•</span>
              <div className="flex items-center gap-1">
                <Users className="w-3.5 h-3.5 shrink-0" />
                <span>{occupantsDisplay}</span>
              </div>
            </div>
          </div>
        </button>
      )}

      {/* Expanded View - Edit form */}
      {isExpanded && (
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">
              {t("searchEditCriteria")}
            </h3>
            <button
              onClick={() => {
                setIsExpanded(false);
                setLocalCriteria(criteria);
              }}
              className="p-1 hover:bg-gray-100 rounded-full"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Form Fields */}
          <div className="space-y-4">
            {/* Location Row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Prefecture */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  {t("searchPrefecture")}
                </label>
                <CustomSelect
                  value={localCriteria.prefecture.toLowerCase()}
                  onChange={(val) =>
                    setLocalCriteria((prev) => ({
                      ...prev,
                      prefecture:
                        String(val) === "tokyo" ? "Tokyo" : String(val),
                    }))
                  }
                  options={[{ value: "tokyo", label: "Tokyo" }]}
                  compact
                />
              </div>

              {/* City */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  {t("searchCity")}
                </label>
                <CustomSelect
                  value={localCriteria.city.toLowerCase()}
                  onChange={(val) =>
                    setLocalCriteria((prev) => ({
                      ...prev,
                      city: String(val) === "tokyo" ? "Tokyo" : String(val),
                    }))
                  }
                  options={[{ value: "tokyo", label: "Tokyo" }]}
                  compact
                />
              </div>

              {/* Ward */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  {t("searchLocation")}
                </label>
                <CustomSelect
                  value={localCriteria.ward}
                  onChange={(val) =>
                    setLocalCriteria((prev) => ({ ...prev, ward: String(val) }))
                  }
                  options={[
                    { value: "", label: t("searchSelectArea") },
                    ...TOKYO_WARDS.map((ward) => ({
                      value: ward.nameEn,
                      label: ward.nameEn,
                    })),
                  ]}
                  placeholder={t("searchSelectArea")}
                  compact
                />
              </div>
            </div>

            {/* Date and Occupants Row - brick 2-col */}
            <div className="grid grid-cols-2 gap-3">
              {/* Move-in Date */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  {t("searchMoveIn")}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                    <Calendar className="w-4 h-4 text-gray-400" />
                  </div>
                  <DatePicker
                    selected={localCriteria.moveInDate}
                    onChange={(date: Date | null) =>
                      setLocalCriteria((prev) => ({
                        ...prev,
                        moveInDate: date,
                      }))
                    }
                    minDate={today}
                    placeholderText={t("searchMoveInPlaceholder")}
                    dateFormat="MMM d, yyyy"
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 focus:outline-none cursor-pointer"
                    calendarClassName="apaato-calendar"
                    isClearable
                  />
                </div>
              </div>

              {/* Occupants */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  {t("searchOccupants")}
                </label>
                <CustomSelect
                  value={localCriteria.occupants}
                  onChange={(val) =>
                    setLocalCriteria((prev) => ({
                      ...prev,
                      occupants: Number(val),
                    }))
                  }
                  options={OCCUPANT_OPTIONS.map((opt) => ({
                    value: opt.value,
                    label:
                      opt.value === 5
                        ? `${opt.label} ${t("searchPeople")}`
                        : `${opt.label} ${opt.value === 1 ? t("searchPerson") : t("searchPeople")}`,
                  }))}
                  compact
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={handleClear}
              className="text-sm text-gray-600 hover:text-gray-900 underline"
            >
              {t("filterReset")}
            </button>
            <button
              onClick={handleApply}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
            >
              <Search className="w-4 h-4" />
              {t("searchButton")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchBar;
